
import { universalStorage } from './universalStorage';
import { logger } from '@/utils/logging';

export interface MediaFile {
  id: string;
  name: string;
  type: string;
  size: number;
  data: string; // base64 encoded data
  section: 'students' | 'teachers' | 'admins' | 'timetable' | 'reports';
  category: 'images' | 'documents';
  uploadDate: string;
  metadata?: {
    width?: number;
    height?: number;
    originalName?: string;
    description?: string;
  };
}

class MediaStorageService {
  
  async saveMedia(file: MediaFile): Promise<void> {
    try {
      const mediaKey = `${file.section}_media_${file.category}`;
      const existingMedia = await universalStorage.getItem<MediaFile[]>(mediaKey) || [];
      
      // Remove existing file with same id if it exists
      const filteredMedia = existingMedia.filter(m => m.id !== file.id);
      filteredMedia.push(file);
      
      await universalStorage.setItem(mediaKey, filteredMedia);
      
      logger.info('Media file saved successfully', {
        component: 'MediaStorage',
        data: {
          section: file.section,
          category: file.category,
          fileName: file.name,
          fileSize: file.size
        }
      });
    } catch (error) {
      logger.error('Failed to save media file', {
        component: 'MediaStorage',
        data: { section: file.section, category: file.category }
      }, error as Error);
      throw error;
    }
  }

  async getMedia(section: string, category: 'images' | 'documents'): Promise<MediaFile[]> {
    try {
      const mediaKey = `${section}_media_${category}`;
      return await universalStorage.getItem<MediaFile[]>(mediaKey) || [];
    } catch (error) {
      logger.error('Failed to get media files', {
        component: 'MediaStorage',
        data: { section, category }
      }, error as Error);
      return [];
    }
  }

  async deleteMedia(section: string, category: 'images' | 'documents', fileId: string): Promise<void> {
    try {
      const mediaKey = `${section}_media_${category}`;
      const existingMedia = await universalStorage.getItem<MediaFile[]>(mediaKey) || [];
      const filteredMedia = existingMedia.filter(m => m.id !== fileId);
      
      await universalStorage.setItem(mediaKey, filteredMedia);
      
      logger.info('Media file deleted successfully', {
        component: 'MediaStorage',
        data: { section, category, fileId }
      });
    } catch (error) {
      logger.error('Failed to delete media file', {
        component: 'MediaStorage',
        data: { section, category, fileId }
      }, error as Error);
      throw error;
    }
  }

  // Enhanced method for saving student profile images with better persistence
  async saveStudentProfileImage(studentId: number, imageData: string): Promise<string> {
    try {
      // Validate base64 image data
      if (!this.isValidBase64Image(imageData)) {
        throw new Error('Invalid image data format');
      }

      const mediaFile: MediaFile = {
        id: `student_profile_${studentId}`,
        name: `profile_${studentId}.jpg`,
        type: 'image/jpeg',
        size: imageData.length,
        data: imageData,
        section: 'students',
        category: 'images',
        uploadDate: new Date().toISOString(),
        metadata: {
          description: `Profile image for student ${studentId}`
        }
      };

      await this.saveMedia(mediaFile);
      
      // Also save to a dedicated profile images storage for faster access
      const profileImagesKey = 'student_profile_images';
      const existingProfileImages = await universalStorage.getItem<Record<string, string>>(profileImagesKey) || {};
      existingProfileImages[studentId.toString()] = imageData;
      await universalStorage.setItem(profileImagesKey, existingProfileImages);

      logger.info('Student profile image saved successfully', {
        component: 'MediaStorage',
        data: { studentId, imageSize: imageData.length }
      });

      return imageData;
    } catch (error) {
      logger.error('Failed to save student profile image', {
        component: 'MediaStorage',
        data: { studentId }
      }, error as Error);
      throw error;
    }
  }

  async getStudentProfileImage(studentId: number): Promise<string | undefined> {
    try {
      // First try to get from dedicated profile images storage
      const profileImagesKey = 'student_profile_images';
      const profileImages = await universalStorage.getItem<Record<string, string>>(profileImagesKey) || {};
      const directImage = profileImages[studentId.toString()];
      
      if (directImage && this.isValidBase64Image(directImage)) {
        return directImage;
      }

      // Fallback to media storage
      const studentMedia = await this.getMedia('students', 'images');
      const profileImage = studentMedia.find(m => m.id === `student_profile_${studentId}`);
      
      if (profileImage && this.isValidBase64Image(profileImage.data)) {
        // Update the direct storage for faster future access
        profileImages[studentId.toString()] = profileImage.data;
        await universalStorage.setItem(profileImagesKey, profileImages);
        return profileImage.data;
      }

      return undefined;
    } catch (error) {
      logger.error('Failed to get student profile image', {
        component: 'MediaStorage',
        data: { studentId }
      }, error as Error);
      return undefined;
    }
  }

  private isValidBase64Image(data: string): boolean {
    if (!data || typeof data !== 'string') return false;
    
    // Check if it's a valid data URL
    if (!data.startsWith('data:image/')) return false;
    
    try {
      const [header, base64Data] = data.split(',');
      if (!base64Data || base64Data.length === 0) return false;
      
      // Validate mime type
      const mimeMatch = header.match(/data:image\/(jpeg|jpg|png|gif|webp);base64/);
      if (!mimeMatch) return false;
      
      // Test base64 validity
      atob(base64Data);
      return true;
    } catch (error) {
      return false;
    }
  }

  async compressImage(base64String: string, maxSizeKB: number = 500): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        
        // Calculate new dimensions to maintain aspect ratio
        const maxDimension = 1200;
        let { width, height } = img;
        
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality levels to meet size requirement
        let quality = 0.8;
        let result = canvas.toDataURL('image/jpeg', quality);
        
        while (result.length > maxSizeKB * 1024 * 4/3 && quality > 0.1) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        
        resolve(result);
      };
      img.src = base64String;
    });
  }
}

export const mediaStorage = new MediaStorageService();
